import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Instagram, MapPin, ArrowRight, BookOpen, Download, Phone, Clock, MessageCircle, Sparkles } from 'lucide-react';
import { OGEET_MENU, OGEET_MENU_PDF } from '../data/ogeetMenu';
import { trackWhatsapp, trackAction } from '../lib/analytics';

const OGEET_LOGO =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/v4l14fbp_ChatGPT%20Image%20May%205%2C%202026%2C%2010_48_05%20PM.png';
const OGEET_BG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/qiqyydwv_file_00000000cfa071f493330a6835986159.png';

// New OGeet imagery
const IMG_SHARING_BOX =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/8seburdm_file_00000000532471f4a228ee41238a6336.png';
const IMG_CURRY_FEAST =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/al00obhd_file_00000000008471f48950f122efe0c30e.png';
const IMG_TERRACE =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/yqqptckn_file_000000009ec871f491c4a4451afa11a6.png';
const IMG_INTERIOR =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/67i3dxzz_file_00000000be2871f49468f0d79cef7247.png';

const OGEET_LAT = -25.776251;
const OGEET_LNG = 28.255513;
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${OGEET_LAT},${OGEET_LNG}`;

// WhatsApp booking — Gita Jivan
const OGEET_WA_LINK = `https://wa.me/27835450004?text=${encodeURIComponent(
  "Hi Gita, I'd like to book a table at OGeet Café & Lounge.",
)}`;

const SECTIONS = [
  {
    eyebrow: 'Small Plates · Big Stories',
    title: 'Bites for the curious.',
    body:
      'Crispy samosas, smoky chillies, and tucked-away treasures — a tasting box made to be opened slowly, shared loudly.',
    image: IMG_INTERIOR,
    align: 'left',
    accent: '#FF1493',
    testid: 'ogeet-section-small-plates',
  },
  {
    eyebrow: 'The Spread',
    title: 'Curries with a chorus.',
    body:
      'Butter chicken, tikka masala, dal makhani — paired with hand-stretched naan, fluffy basmati, and the kind of conversation that lasts past midnight.',
    image: IMG_TERRACE,
    align: 'right',
    accent: '#FFB300',
    testid: 'ogeet-section-spread',
  },
  {
    eyebrow: 'Evenings on 16th',
    title: 'Under the lanterns.',
    body:
      'Fairy-lit trees, sunset over the boulevard, soft cushions and the buzz of the neighbourhood. Our terrace is where the night begins.',
    image: IMG_CURRY_FEAST,
    align: 'left',
    accent: '#6A1B9A',
    testid: 'ogeet-section-terrace',
  },
  {
    eyebrow: 'Step Inside',
    title: 'Where the colour lives.',
    body:
      'Hanging baskets, rattan pendants, and a wall of light — an interior that feels equal parts garden, lounge, and celebration.',
    image: IMG_SHARING_BOX,
    align: 'right',
    accent: '#00BFA5',
    testid: 'ogeet-section-interior',
  },
];

const OGeetPage = () => {
  const [activeCat, setActiveCat] = useState(OGEET_MENU[0].id);
  const currentCat = OGEET_MENU.find((c) => c.id === activeCat) || OGEET_MENU[0];

  useEffect(() => {
    // Allow scroll on this page
    document.body.style.overflow = '';
    return () => {};
  }, []);

  return (
    <main data-testid="ogeet-page" className="bg-[#1A0F08] text-[#FFF8E1] overflow-x-hidden">
      {/* ============ HERO ============ */}
      <section className="relative w-screen min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={OGEET_BG} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-black/85 via-black/55 lg:via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-black/15" />
        </div>

        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#FF1493] opacity-15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 left-1/3 w-[28rem] h-[28rem] rounded-full bg-[#FFB300] opacity-15 blur-3xl pointer-events-none" />

        <Link
          to="/"
          data-testid="back-to-landing"
          className="absolute top-6 left-6 md:top-8 md:left-8 z-20 flex items-center gap-2 text-[#FFF8E1] hover:text-[#FFD54F] transition-colors font-['Cormorant_Garamond'] tracking-[0.2em] uppercase text-xs md:text-sm group"
        >
          <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
          Back
        </Link>

        <Link
          to="/geet"
          data-testid="visit-geet"
          className="absolute top-6 right-6 md:top-8 md:right-8 z-20 text-[#FFF8E1]/85 hover:text-[#FFD54F] transition-colors font-['Cormorant_Garamond'] italic tracking-wider text-xs md:text-sm"
        >
          Visit Geet Restaurant →
        </Link>

        <div className="relative z-10 w-full max-w-2xl px-6 md:px-12 lg:ml-16 lg:mr-auto lg:max-w-xl text-center lg:text-left py-24 lg:py-0">
          <div className="flex items-center gap-3 mb-6 justify-center lg:justify-start">
            <span className="h-px w-10 bg-[#FFD54F]" />
            <span className="flex items-center gap-2 font-['Cormorant_Garamond'] italic text-[#FFD54F] text-sm tracking-[0.35em] uppercase">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD54F] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FFD54F]" />
              </span>
              Now Open
            </span>
            <span className="h-px w-10 bg-[#FFD54F]" />
          </div>

          <div className="mx-auto lg:mx-0 mb-5 flex justify-center lg:justify-start">
            <img
              src={OGEET_LOGO}
              alt="OGeet Café & Lounge"
              data-testid="ogeet-logo"
              className="h-40 md:h-52 lg:h-60 w-auto drop-shadow-[0_15px_50px_rgba(0,0,0,0.6)]"
              style={{ mixBlendMode: 'screen' }}
            />
          </div>

          <h1 className="font-['Marcellus'] text-3xl md:text-5xl lg:text-6xl text-[#FFF8E1] mb-4 leading-tight drop-shadow-[0_6px_20px_rgba(0,0,0,0.6)]">
            The doors are
            <br />
            <span className="italic font-['Cormorant_Garamond'] text-[#FFD54F]">open.</span>
          </h1>

          <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FFF8E1]/95 max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed drop-shadow">
            From the heart behind <span className="italic text-[#FFD54F]">Geet</span> — OGeet Café &amp;
            Lounge is now serving bold Indian flavour, late-night cocktails, and the colour of every
            Indian festival.
          </p>

          <div className="inline-block bg-black/40 backdrop-blur-md border border-[#FFD54F]/40 rounded-xl px-5 py-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <MapPin size={20} className="text-[#FFD54F] mt-1 flex-shrink-0" />
              <div>
                <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.25em] text-[#FFD54F]/90 text-xs mb-1">
                  Find Us
                </p>
                <p
                  data-testid="ogeet-address"
                  className="font-['Cormorant_Garamond'] text-lg text-[#FFF8E1] leading-snug"
                >
                  Shop 1, No 15<br />
                  16th Street
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-5 justify-center lg:justify-start">
            <Link
              to="/ogeet/set-menu"
              data-testid="ogeet-hero-setmenu"
              className="group inline-flex items-center gap-3 bg-[#FF1493] text-white px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#d1127e] transition-all duration-300 shadow-[0_10px_30px_rgba(255,20,147,0.4)]"
            >
              <Sparkles size={16} />
              Restaurant Week Set Menu
            </Link>

            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ogeet-directions"
              onClick={() => trackAction('directions', 'ogeet')}
              className="group inline-flex items-center gap-3 bg-[#FFD54F] text-[#1C1917] px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FFF8E1] transition-all duration-300 shadow-lg"
            >
              Get Directions
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>

            <a
              href="#menu"
              data-testid="ogeet-hero-menu-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group inline-flex items-center gap-3 border-2 border-[#FFD54F]/70 text-[#FFD54F] bg-[#FFD54F]/10 backdrop-blur-sm px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FFD54F] hover:text-[#1C1917] transition-all duration-300"
            >
              <BookOpen size={16} />
              Browse Menu
            </a>

            <a
              href={OGEET_WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ogeet-hero-whatsapp"
              onClick={() => trackWhatsapp('ogeet')}
              title="WhatsApp Gita Jivan · +27 83 545 0004"
              className="group inline-flex items-center gap-3 bg-[#25D366] text-[#0B3B22] px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#1EBE57] transition-all duration-300 shadow-lg"
            >
              <MessageCircle size={16} />
              Book via WhatsApp
            </a>

            <div className="flex items-center gap-5 text-[#FFF8E1]/85">
              <a
                href="https://www.instagram.com/geet_indianrestaurant/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OGeet on Instagram"
                data-testid="ogeet-instagram"
              onClick={() => trackAction('instagram', 'ogeet')}
                className="hover:text-[#FFD54F] transition-colors"
              >
                <Instagram size={22} />
              </a>
              <span className="font-['Cormorant_Garamond'] italic text-sm tracking-wider">Pretoria</span>
            </div>
          </div>

          {/* Scroll cue */}
          <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#FFD54F]/60">
            <span className="font-['Cormorant_Garamond'] italic tracking-[0.4em] uppercase text-[10px]">
              Scroll
            </span>
            <span className="h-12 w-px bg-gradient-to-b from-[#FFD54F]/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* ============ MOMENTS CTA ============ */}
      <section
        data-testid="ogeet-moments-cta"
        className="relative py-10 md:py-14 bg-[#1A0F08] border-t border-[#FFD54F]/15 overflow-hidden"
      >
        <div className="absolute -top-24 right-1/4 w-80 h-80 rounded-full bg-[#FF1493] opacity-20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 left-1/4 w-80 h-80 rounded-full bg-[#6A1B9A] opacity-20 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
          {/* Decorative floating polaroids */}
          <div className="hidden md:flex items-end justify-center gap-4 shrink-0">
            {[
              { img: IMG_TERRACE, tilt: -8 },
              { img: IMG_SHARING_BOX, tilt: 5 },
              { img: IMG_INTERIOR, tilt: -3 },
            ].map(({ img, tilt }, i) => (
              <div
                key={i}
                style={{ transform: `rotate(${tilt}deg) translateY(${i === 1 ? -6 : 0}px)` }}
                className="bg-[#FFF8E1] p-1.5 pb-4 rounded-sm shadow-[0_12px_28px_rgba(0,0,0,0.5)] w-16 h-20 relative"
              >
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${img})` }}
                />
              </div>
            ))}
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
              <span className="h-px w-10 bg-[#FFD54F]" />
              <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.35em] uppercase text-[#FFD54F]">
                The Moments
              </span>
            </div>

            <h2 className="font-['Marcellus'] text-2xl md:text-4xl text-[#FFF8E1] mb-3 leading-tight">
              Peek behind <span className="italic font-['Cormorant_Garamond'] text-[#FFD54F]">the curtain.</span>
            </h2>

            <p className="font-['Cormorant_Garamond'] italic text-base md:text-lg text-[#FFF8E1]/80 max-w-2xl mb-5 leading-relaxed">
              Live OGeet through our lens — evenings, plates, faces, and the little in-between moments,
              pinned to the wall.
            </p>

            <Link
              to="/ogeet/moments"
              data-testid="ogeet-moments-btn"
              className="group inline-flex items-center gap-3 bg-[#FFD54F] text-[#1C1917] px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-full hover:bg-[#FFF8E1] transition-all duration-300 shadow-lg"
            >
              Enter the Moments
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>


      {/* ============ FEATURE SECTIONS ============ */}
      {SECTIONS.map((s, idx) => (
        <section
          key={s.testid}
          data-testid={s.testid}
          className="relative py-24 md:py-32 overflow-hidden"
        >
          {/* Festival accent glow per section */}
          <div
            className="absolute -top-40 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{
              background: s.accent,
              left: s.align === 'left' ? '-10%' : 'auto',
              right: s.align === 'right' ? '-10%' : 'auto',
            }}
          />

          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
            <div
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                s.align === 'right' ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              {/* Image */}
              <div className="lg:col-span-7 relative group">
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.6)]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                {/* Decorative number */}
                <div
                  className={`hidden md:block absolute font-['Marcellus'] text-[10rem] leading-none opacity-15 select-none pointer-events-none ${
                    s.align === 'right' ? '-left-6 -top-12' : '-right-6 -top-12'
                  }`}
                  style={{ color: s.accent }}
                >
                  0{idx + 1}
                </div>
              </div>

              {/* Text */}
              <div
                className={`lg:col-span-5 ${
                  s.align === 'right' ? 'lg:text-right' : 'lg:text-left'
                } text-center`}
              >
                <div
                  className={`flex items-center gap-3 mb-5 ${
                    s.align === 'right' ? 'lg:justify-end' : 'lg:justify-start'
                  } justify-center`}
                >
                  <span className="h-px w-10" style={{ background: s.accent }} />
                  <span
                    className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase"
                    style={{ color: s.accent }}
                  >
                    {s.eyebrow}
                  </span>
                </div>

                <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#FFF8E1] mb-6 leading-tight">
                  {s.title}
                </h2>

                <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FFF8E1]/80 leading-relaxed max-w-md mx-auto lg:mx-0">
                  {s.body}
                </p>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ============ MENU ============ */}
      <section
        id="menu"
        data-testid="ogeet-menu-section"
        className="relative py-24 md:py-32 bg-[#0F0805] border-t border-[#FFD54F]/15 overflow-hidden"
      >
        <div className="absolute -top-40 left-1/4 w-[32rem] h-[32rem] rounded-full bg-[#FF1493] opacity-15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 right-1/4 w-[32rem] h-[32rem] rounded-full bg-[#FFB300] opacity-15 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          {/* Header */}
          <div className="text-center mb-12 md:mb-14">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD54F]" />
              <span className="font-['Cormorant_Garamond'] italic text-[#FFD54F] text-sm tracking-[0.35em] uppercase">
                The Menu
              </span>
              <span className="h-px w-12 bg-[#FFD54F]" />
            </div>
            <h2 className="font-['Marcellus'] text-3xl md:text-5xl lg:text-6xl text-[#FFF8E1] mb-5 leading-tight">
              Eat the rainbow,
              <br />
              <span className="italic font-['Cormorant_Garamond'] text-[#FFD54F]">drink the festival.</span>
            </h2>
            <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FFF8E1]/80 max-w-2xl mx-auto">
              Eighteen chapters of bites, plates, pours and sweet finishes — flick through, or grab the full PDF.
            </p>
          </div>

          {/* Download PDF + scroll hint */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <a
              href={OGEET_MENU_PDF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ogeet-menu-pdf"
              onClick={() => trackAction('menu_download', 'ogeet')}
              className="group inline-flex items-center gap-3 bg-[#FFD54F] text-[#1C1917] px-7 py-3 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-full hover:bg-[#FFF8E1] transition-all duration-300 shadow-lg"
            >
              <Download size={16} />
              Download PDF
            </a>
            <a
              href={OGEET_MENU_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#FFD54F]/50 text-[#FFD54F] px-6 py-3 font-['Cormorant_Garamond'] italic tracking-wider text-sm rounded-full hover:bg-[#FFD54F]/10 transition-all duration-300"
            >
              Or view the full PDF →
            </a>
          </div>

          {/* Sticky category pills */}
          <div className="sticky top-4 z-20 -mx-6 md:-mx-12 lg:-mx-20 px-6 md:px-12 lg:px-20 mb-12 py-5 md:py-6 bg-[#0F0805]/95 backdrop-blur-md border-y border-[#FFD54F]/15">
            <div className="flex overflow-x-auto gap-2 md:gap-3 scrollbar-hide pb-2">
              {OGEET_MENU.map((cat) => {
                const active = activeCat === cat.id;
                return (
                  <button
                    key={cat.id}
                    data-testid={`ogeet-menu-cat-${cat.id}`}
                    onClick={() => setActiveCat(cat.id)}
                    className={`px-4 py-2 rounded-full whitespace-nowrap transition-all duration-300 border font-['Cormorant_Garamond'] text-sm md:text-base tracking-wide ${
                      active
                        ? 'bg-[#FFD54F] text-[#1C1917] border-[#FFD54F] shadow-[0_0_25px_rgba(255,213,79,0.45)]'
                        : 'bg-transparent text-[#FFF8E1]/75 border-[#FFD54F]/30 hover:border-[#FFD54F] hover:text-[#FFF8E1]'
                    }`}
                  >
                    {cat.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category content */}
          <div data-testid={`ogeet-menu-cat-content-${currentCat.id}`}>
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className="h-px w-8" style={{ background: currentCat.accent }} />
                <span
                  className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase"
                  style={{ color: currentCat.accent }}
                >
                  {currentCat.eyebrow}
                </span>
                <span className="h-px w-8" style={{ background: currentCat.accent }} />
              </div>
              <h3 className="font-['Marcellus'] text-2xl md:text-4xl text-[#FFF8E1] mb-3 tracking-wide">
                {currentCat.title}
              </h3>
              {currentCat.pricing && (
                <p className="font-['Cormorant_Garamond'] italic text-base md:text-lg text-[#FFD54F]/90">
                  {currentCat.pricing}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-7">
              {currentCat.items.map((item, idx) => (
                <div
                  key={idx}
                  data-testid={`ogeet-menu-item-${currentCat.id}-${idx}`}
                  className="group border-b border-[#FFD54F]/20 pb-5 hover:border-[#FFD54F]/60 transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-4 mb-1">
                    <h4 className="font-['Marcellus'] text-lg md:text-xl text-[#FFF8E1] tracking-wide leading-tight">
                      {item.name}
                    </h4>
                    {item.price && (
                      <span className="font-['Marcellus'] text-base md:text-lg text-[#FFD54F] whitespace-nowrap flex-shrink-0">
                        {item.price}
                      </span>
                    )}
                  </div>
                  {item.desc && (
                    <p className="font-['Cormorant_Garamond'] text-base md:text-lg text-[#FFF8E1]/65 leading-relaxed">
                      {item.desc}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ BOOKINGS ============ */}
      <section
        id="bookings"
        data-testid="ogeet-bookings"
        className="relative py-24 md:py-28 bg-[#1A0F08] border-t border-[#FFD54F]/15 overflow-hidden"
      >
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#FF1493] opacity-15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#FFB300] opacity-15 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#FFD54F]" />
              <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#FFD54F]">
                Reserve a Table
              </span>
              <span className="h-px w-10 bg-[#FFD54F]" />
            </div>
            <h2 className="font-['Marcellus'] text-3xl md:text-5xl text-[#FFF8E1] mb-5 leading-tight">
              Save your seat <span className="italic font-['Cormorant_Garamond'] text-[#FFD54F]">in Royal India.</span>
            </h2>
            <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FFF8E1]/80 max-w-2xl mx-auto">
              Bookings recommended — give us a ring, and we&apos;ll make space for the celebration.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3">
              <a
                href={OGEET_WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ogeet-bookings-whatsapp"
                onClick={() => trackWhatsapp('ogeet')}
                title="WhatsApp Gita Jivan · +27 83 545 0004"
                className="group inline-flex items-center gap-3 bg-[#25D366] text-[#0B3B22] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-full hover:bg-[#1EBE57] transition-all duration-300 shadow-lg"
              >
                <MessageCircle size={18} />
                Book via WhatsApp
              </a>
              <p
                data-testid="ogeet-whatsapp-number"
                className="font-['Cormorant_Garamond'] italic text-[#FFF8E1]/75 text-sm tracking-wider"
              >
                Or message Gita Jivan on{' '}
                <a
                  href={OGEET_WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FFD54F] hover:text-[#FFF8E1] not-italic tracking-wide"
                >
                  +27 83 545 0004
                </a>
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Phone card */}
            <div
              data-testid="ogeet-phone-card"
              className="bg-black/40 backdrop-blur-md border border-[#FFD54F]/30 rounded-2xl p-7 md:p-9 hover:border-[#FFD54F] transition-all duration-500"
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FFD54F]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FFD54F]/40">
                  <Phone className="text-[#FFD54F]" size={26} />
                </div>
                <div>
                  <p className="font-['Cormorant_Garamond'] italic text-xs tracking-[0.3em] uppercase text-[#FFD54F]/85 mb-2">
                    Call to Book
                  </p>
                  <a
                    href="tel:0124603199"
                    data-testid="ogeet-phone-link"
                    className="block font-['Marcellus'] text-3xl md:text-4xl text-[#FFF8E1] hover:text-[#FFD54F] transition-colors tracking-wider mb-4"
                  >
                    012 460 3199
                  </a>
                  <span className="inline-block border border-[#FFD54F]/50 rounded-full px-4 py-1.5 font-['Cormorant_Garamond'] italic text-[#FFD54F] text-xs tracking-[0.25em] uppercase">
                    Reservations Recommended
                  </span>
                </div>
              </div>
            </div>

            {/* Hours card */}
            <div
              data-testid="ogeet-hours-card"
              className="bg-black/40 backdrop-blur-md border border-[#FFD54F]/30 rounded-2xl p-7 md:p-9"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#FFD54F]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FFD54F]/40">
                  <Clock className="text-[#FFD54F]" size={24} />
                </div>
                <div>
                  <p className="font-['Cormorant_Garamond'] italic text-xs tracking-[0.3em] uppercase text-[#FFD54F]/85">
                    Doors Open
                  </p>
                  <h3 className="font-['Marcellus'] text-2xl text-[#FFF8E1] tracking-wide">
                    Hours
                  </h3>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { day: 'Monday – Saturday', time: '10am – 10pm' },
                  { day: 'Sunday', time: '10am – 4pm' },
                ].map((row) => (
                  <div
                    key={row.day}
                    className="flex justify-between items-center py-2 border-b border-[#FFD54F]/15 last:border-0"
                  >
                    <span className="font-['Cormorant_Garamond'] text-base md:text-lg text-[#FFF8E1]">
                      {row.day}
                    </span>
                    <span className="font-['Cormorant_Garamond'] text-base md:text-lg text-[#FFD54F]">
                      {row.time}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-5 text-[#FFF8E1]/70">
                <MapPin size={16} className="text-[#FFD54F]" />
                <p className="font-['Cormorant_Garamond'] italic text-sm md:text-base">
                  Shop 1, No 15, 16th Street · Pretoria
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA FOOTER ============ */}
      <section className="relative py-20 md:py-28 border-t border-[#FFD54F]/20 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#FF1493] opacity-20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#FFB300] opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[#FFD54F] text-sm tracking-[0.35em] uppercase mb-5">
            ✦  Come Find Us  ✦
          </p>
          <h2 className="font-['Marcellus'] text-3xl md:text-5xl text-[#FFF8E1] mb-6 leading-tight">
            See you under <span className="italic text-[#FFD54F]">the lanterns.</span>
          </h2>
          <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FFF8E1]/80 mb-10">
            Shop 1, No 15, 16th Street · Pretoria
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ogeet-directions-footer"
              onClick={() => trackAction('directions', 'ogeet')}
              className="group inline-flex items-center gap-3 bg-[#FFD54F] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FFF8E1] transition-all duration-300 shadow-lg"
            >
              Get Directions
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="https://www.instagram.com/geet_indianrestaurant/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ogeet-instagram-footer"
              onClick={() => trackAction('instagram', 'ogeet')}
              className="inline-flex items-center gap-2 border border-[#FFD54F]/50 text-[#FFD54F] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg hover:bg-[#FFD54F]/10 transition-all duration-300"
            >
              <Instagram size={18} />
              Follow
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OGeetPage;
